import {
  Button,
  Card,
  Modal,
  notification,
  Pagination,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import PageTitle from 'components/page-title';
import { SERVER_FILE_ENDPOINT } from 'constants/envs';
import React, { useEffect, useState } from 'react';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import styles from './styles.module.scss';
import CustomButton from 'components/custom-button';
import { ColumnsType } from 'antd/lib/table';
import { formatedDate } from '../../utils/common';
import moment from 'moment';
import { LiveShowFilter } from '../liveshows-manager';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import ModalCreateImageAds from './component/ModalCreateImageAds';
import { describeDailyWindow, formatVn, ImageAdItem, RUN_STATUS } from './ad-schedule';

const LIMIT = 10;

const initFilter = { page: 1, limit: LIMIT };

const AdsManager = () => {
  const [isOpenModalEditAndCreate, setIsOpenModalEditAndCreate] = useState(false);
  const [editingRecord, setEditingRecord] = useState<ImageAdItem | null>(null);
  const [togglingIds, setTogglingIds] = useState<number[]>([]);
  // GET /ads: `enabled` = công tắc tổng; `isDisplay` = app có bật popup lúc này không
  // (tắt khi không có ảnh nào đang chạy). Server cũ chưa có `enabled` → dùng isDisplay.
  const [ads, setAds] = useState<{ isDisplay: boolean; enabled?: boolean; runningCount?: number }>({
    isDisplay: false,
  });
  const adsEnabled = ads?.enabled ?? !!ads?.isDisplay;
  const [statusUpdateData, setStatusUpdateData] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [dataResponse, setDataResponse] = useState<any>({
    items: [],
    totalCount: 0,
  });
  const [filter, setFilter] = useState(initFilter as LiveShowFilter);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);

  useEffect(() => {
    const fetchAds = async () => {
      try {
        const res: any = await AxiosUtils.get(API.ads);
        setAds(res?.data);
      } catch (error) {
        notification.error({
          message: 'Không thành công',
          description: 'Có lỗi xảy ra, vui lòng thử lại.',
          duration: 4,
        });
      }
    };

    const fetchListImageAds = async () => {
      setLoading(true);
      try {
        const res: any = await AxiosUtils.get(API.listAds, {
          ...filter,
        });
        setDataResponse({ items: res?.data?.data, totalCount: res?.data?.metadata?.total });
      } finally {
        setLoading(false);
      }
    };

    fetchAds();
    fetchListImageAds();
    // `filter` trong deps: trước đây đổi trang không tải lại danh sách.
  }, [statusUpdateData, filter]);

  const handleOkDeleteModalAds = async () => {
    setLoading(true);
    try {
      await AxiosUtils._delete(API.deleteAds, { ids: selectedRowKeys });
      onChangeStatusUpdateData();
      notification.success({
        message: 'Thành công',
        description: 'Xóa thành công!',
        duration: 4,
      });
      setSelectedRowKeys([]);
    } catch (error) {
      notification.error({
        message: 'Không thành công',
        description: 'Có lỗi xảy ra, vui lòng thử lại.',
        duration: 4,
      });
    } finally {
      setLoading(false);
      setIsModalDeleteOpen(false);
    }
  };

  const onChangeStatusUpdateData = () => setStatusUpdateData((prev) => !prev);

  const openCreateModal = () => {
    setEditingRecord(null);
    setIsOpenModalEditAndCreate(true);
  };

  const openEditModal = (record: ImageAdItem) => {
    setEditingRecord(record);
    setIsOpenModalEditAndCreate(true);
  };

  const handleToggleImage = async (record: ImageAdItem) => {
    setTogglingIds((ids) => [...ids, record.id]);
    try {
      await AxiosUtils.put(`${API.editAds}/${record.id}`);
      onChangeStatusUpdateData();
    } catch (error) {
      notification.error({
        message: 'Không thành công',
        description: 'Có lỗi xảy ra, vui lòng thử lại.',
        duration: 4,
      });
    } finally {
      setTogglingIds((ids) => ids.filter((id) => id !== record.id));
    }
  };

  // Bảng phải vừa khung 956px (màn 1280, thanh bên mở) để cột Trạng thái/Ngày tạo không
  // nằm khuất dưới cột "Thao tác" ghim bên phải. Vì vậy: liên kết nằm dưới ảnh (một cột co
  // giãn), ngày tạo hai dòng, các cột khác rộng cố định; bảng dàn cố định (tableLayout="fixed")
  // nên link dài không dấu cách cắt "…" trong ô chứ không đẩy cột khác.
  const columns: ColumnsType<ImageAdItem> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 72,
      key: 'id',
    },
    {
      title: 'Ảnh & liên kết',
      dataIndex: 'image',
      key: 'image',
      render: (value, record) => (
        <div className={styles.media}>
          <img
            width={136}
            height={72}
            className={styles.thumb}
            src={`${SERVER_FILE_ENDPOINT}/${value}`}
            alt=""
          />
          {record.url ? (
            <Tooltip title={record.url}>
              <span className={styles.link}>{record.url}</span>
            </Tooltip>
          ) : (
            <span className={styles.scheduleSub}>Không có liên kết</span>
          )}
        </div>
      ),
    },
    {
      title: 'Lịch chạy',
      key: 'schedule',
      // "Kết thúc 30/09/2026 23:59:59" ~212px + 2 × 16px đệm ô.
      width: 248,
      render: (_, record) => (
        <div className={styles.schedule}>
          {record.startAt || record.endAt ? (
            <>
              <div>
                <span className={styles.scheduleSub}>Bắt đầu</span>{' '}
                <span className={styles.nowrap}>
                  {record.startAt ? formatVn(record.startAt) : 'ngay'}
                </span>
              </div>
              <div>
                <span className={styles.scheduleSub}>Kết thúc</span>{' '}
                <span className={styles.nowrap}>
                  {record.endAt ? formatVn(record.endAt) : 'không hẹn'}
                </span>
              </div>
            </>
          ) : (
            <div>Chạy liên tục</div>
          )}
          <div className={styles.scheduleSub}>
            {describeDailyWindow(record.dailyStartTime, record.dailyEndTime)}
          </div>
        </div>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'runStatus',
      key: 'runStatus',
      // "Ngoài khung giờ" ~110px + 2 × 16px đệm ô.
      width: 148,
      render: (value: ImageAdItem['runStatus']) => {
        const status = value ? RUN_STATUS[value] : null;
        if (!status) return null;
        return (
          <Tooltip title={status.hint}>
            <Tag color={status.color} className={styles.tag}>
              {status.label}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      // Ngày một dòng, giờ (24 tiếng như các trang khác) dòng dưới — cột hẹp mà không cắt chữ.
      render: (value) => (
        <div className={styles.nowrap}>
          <div>{formatedDate(value)}</div>
          <div className={styles.scheduleSub}>{moment(value).format('HH:mm')}</div>
        </div>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      // Công tắc 28px + 12px + nút "Sửa" ~65px + 2 × 16px đệm ô.
      width: 144,
      fixed: 'right',
      render: (_, record) => (
        <Space size={12}>
          <Tooltip title={record.isDisplay ? 'Tạm dừng ảnh này' : 'Cho ảnh này chạy lại'}>
            <Switch
              size="small"
              checked={record.isDisplay}
              loading={togglingIds.includes(record.id)}
              onChange={() => handleToggleImage(record)}
            />
          </Tooltip>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)}>
            Sửa
          </Button>
        </Space>
      ),
    },
  ];

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const handleOpenDeleteModal = () => {
    setIsModalDeleteOpen(true);
  };

  const handleChangePage = (page: number) => {
    setFilter((preState: any) => ({ ...preState, page }));
  };

  const handleCancelDeleteModal = () => {
    setIsModalDeleteOpen(false);
  };

  const handleChangeStatusAds = async () => {
    try {
      setToggling(true);
      const res: any = await AxiosUtils.patch(`${API.ads}/1`, { isDisplay: !adsEnabled });
      if (res?.status === 200) {
        setStatusUpdateData(!statusUpdateData);
        notification.success({
          message: 'Thành công',
          description: 'Sửa thành công!',
          duration: 4,
        });
      }
    } catch (e) {
      notification.error({
        message: 'Không thành công',
        description: 'Có lỗi xảy ra, vui lòng thử lại.',
        duration: 4,
      });
    } finally {
      setToggling(false);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };
  const hasSelected = selectedRowKeys.length > 0;

  return (
    <div>
      <PageTitle
        title="Quảng cáo"
        extra={
          <Space wrap>
            {hasSelected && (
              <Button danger icon={<DeleteOutlined />} onClick={handleOpenDeleteModal}>
                Xóa {selectedRowKeys.length} ảnh
              </Button>
            )}
            <CustomButton
              onClick={openCreateModal}
              bgColor="primary-green"
              text="Thêm ảnh quảng cáo"
              icon={<PlusOutlined />}
            />
          </Space>
        }
      />

      <Card className={styles.statusCard} styles={{ body: { padding: 20 } }}>
        <div className={styles.statusRow}>
          <div>
            <div className={styles.statusTitle}>
              Hiện quảng cáo trong app: {adsEnabled ? 'Đang bật' : 'Đang tắt'}
            </div>
            <div className={styles.statusHint}>
              Khi bật, mỗi lần vào Trang chủ app hiện toàn màn hình một ảnh ngẫu nhiên trong các ảnh{' '}
              <b>Đang chạy</b> bên dưới. Mỗi ảnh có lịch riêng: ngày giờ bắt đầu/kết thúc và khung
              giờ trong ngày.
            </div>
            {adsEnabled && ads?.runningCount === 0 && (
              <div className={styles.statusWarn}>
                Lúc này không có ảnh nào đang chạy nên app sẽ không hiện quảng cáo.
              </div>
            )}
          </div>
          <Switch
            checked={adsEnabled}
            loading={toggling}
            checkedChildren="Bật"
            unCheckedChildren="Tắt"
            onChange={handleChangeStatusAds}
          />
        </div>
      </Card>

      <Card
        className="custom-table"
        styles={{ body: { padding: 0 } }}
        style={{ overflow: 'hidden' }}
      >
        <Table
          rowSelection={rowSelection}
          columns={columns}
          rowKey="id"
          pagination={false}
          dataSource={dataResponse.items}
          tableLayout="fixed"
          // 944 = các cột cố định (768) + tối thiểu 176px cho cột ảnh: vừa khung 956px ở màn
          // 1280; chỉ khung hẹp hơn mới cuộn ngang.
          scroll={{ x: 944 }}
          loading={loading}
        />
        {dataResponse.totalCount > LIMIT && (
          <div className={styles.pagination}>
            <Pagination
              showSizeChanger={false}
              current={filter.page}
              onChange={handleChangePage}
              showLessItems
              total={dataResponse.totalCount}
              pageSize={LIMIT}
            />
          </div>
        )}
      </Card>

      {isOpenModalEditAndCreate && (
        <ModalCreateImageAds
          isOpen={isOpenModalEditAndCreate}
          record={editingRecord}
          handleCancelCreateEditModal={() => setIsOpenModalEditAndCreate(false)}
          onChangeStatusUpdateData={onChangeStatusUpdateData}
        />
      )}
      <Modal
        title="Xóa ảnh quảng cáo"
        open={isModalDeleteOpen}
        onOk={handleOkDeleteModalAds}
        onCancel={handleCancelDeleteModal}
        okText="Xóa"
        cancelText="Hủy"
        okButtonProps={{ danger: true }}
        confirmLoading={loading}
      >
        <p>Bạn có chắc muốn xóa {selectedRowKeys.length} ảnh quảng cáo đã chọn?</p>
      </Modal>
    </div>
  );
};

export default AdsManager;
